// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import CheckoutHeaderItem from "@adl/App/Components/Checkout/CheckoutHeaderItem";
import XngLoadingOverlay from "@adl/App/Components/Common/XngLoadingOverlay";
import XngUtilities from "@adl/App/Components/Common/XngUtilities";
import XngZone from "@adl/App/Components/Common/XngZone";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { LogoLinksSearchStyles } from "@adl/Overrides/Widgets/Header/LogoLinksSearch";
import { DivWrapper } from "@adl/XDL/constants";
import { buttonTransparent } from "@adl/XDL/PreStyle/button";
import { color, isMobileScreen, layoutContainer, xScreen } from "@adl/XDL/Utilities";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconWrapper } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import styled, { css, ThemeProps } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    // <XNG-Changes>
    const pageType = getCurrentPage(state).type;
    // </XNG-Changes>
    return {
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        isTabletScreen: state.xCommon.isTabletScreen,
        registerMenu: state.xHeader.registerMenu,
        isSticky: state.xHeader.isSticky,
        currentLocation: getLocation(state),
        // <XNG-Changes>
        isCheckoutPage: pageType === "CheckoutShippingPage" || pageType === "CheckoutReviewAndSubmitPage",
        isSmallMobileScreen: state.xCommon.isSmallMobileScreen,
        isMobileScreen: state.xCommon.isMobileScreen,
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
    setInitialValues,
    setNavDrawerIsOpen,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        background: "common.white",
        css: css`
            position: relative;
            z-index: ${getProp("theme.zIndex.header")};
            background: ${getColor("common.white")};
            ${xScreen("lte", "sm")} {
                position: sticky;
                top: 0;
            }
        `,
    },
    container: {
        css: css`
            background-color: ${getColor("primary")};
            display: flex;
            > div {
                ${layoutContainer}
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
            }

            > div.desktop-view {
                > div {
                    flex: 1;
                }
                > div:first-child {
                    flex: 2;
                }
                ${xScreen("gte", 1160)} {
                    padding-left: 24px;
                }
            }

            ${xScreen("lte", "md")} {
                &&& > div {
                    padding: 12px 16px;
                    width: 100%;
                    display: flex;
                    align-items: center;
                }
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

export const logoLinksSearchStyles: LogoLinksSearchStyles = {
    container: {
        css: css`
            display: flex;
            align-items: center;
            padding-top: 20px;
            padding-bottom: 20px;
            ${layoutContainer}
            ${({ isCheckoutPage }: any) => (isCheckoutPage ? `box-shadow: 0 1px 0 0 rgba(0,0,0,0.17);` : "")}
            ${xScreen("lte", "xs")} {
                &&&& {
                    padding: 12px 16px;
                }
            }
        `,
    },
    actionsContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-grow: 1;
            ${xScreen("gte", "665")} {
                flex-wrap: wrap;
            }
        `,
    },
    logoWrapper: {
        css: css`
            margin-right: 70px;
            img {
                height: 55px;
                width: 150px;
            }
            ${xScreen("lte", "md")} {
                margin-right: 16px;
                img {
                    height: 40px;
                    width: 112px;
                }
            }

            @media print {
                display: block;
                padding-bottom: 20px;
                padding-left: 25px;
                margin-top: 5px;
            }
        `,
    },
    searchMenuWrapper: {
        css: css`
            text-align: right;
            flex-basis: 100%;
            margin-bottom: 15px;
            & > ${DivWrapper} {
                display: flex;
                justify-content: space-between;
                align-items: center;
                & > div:not(:last-child) {
                    display: flex;
                    margin-right: 20px;
                }
            }
        `,
    },
    searchWrapper: {
        css: css`
            flex-grow: 1;
            max-width: 695px;
            margin-right: 70px;
            ${xScreen("lte", "1278")} {
                margin-right: 16px;
            }
            ${xScreen("lte", "665")} {
                margin-right: 0;
            }
        `,
    },
    actionsWrapper: {
        css: css`
            & > ${DivWrapper} {
                display: flex;
                & > div:not(:last-child) {
                    margin-right: 25px;
                }
            }
        `,
    },
    mobile: {
        container: {
            css: css`
                width: 100%;
                display: flex;
                flex-wrap: wrap;
                justify-content: space-between;
                align-items: center;
            `,
        },
        logoMenu: {
            css: css`
                display: flex;
                align-items: center;
                img {
                    max-height: 70px;
                    width: 150px;
                    ${xScreen("lte", "md")} {
                        width: 112px;
                    }
                }
            `,
        },
        searchMenuWrapper: {
            css: css`
                flex-basis: 100%;
                padding-bottom: 8px;
                margin-bottom: 8px;
                position: relative;
                &:before {
                    content: "";
                    background: ${color("common.border")};
                    height: 1px;
                    width: calc(100% + 40px);
                    position: absolute;
                    left: -20px;
                    top: 100%;
                }
            `,
        },
        searchWrapper: {
            css: css`
                display: flex;
                flex-basis: 100%;
                justify-content: center;
                background: ${color("primary")};
                margin-top: 40px;
            `,
        },
        menuTriggerButton: {
            css: css`
                margin-right: 10px;
                padding: 0;
                ${IconWrapper} {
                    color: ${color("primary")};
                }
                ${buttonTransparent}
            `,
        },
        actionsWrapper: {
            css: css`
                & > ${DivWrapper} {
                    display: flex;
                    & > div:not(:last-child) {
                        margin-right: 0;
                    }
                }
            `,
        },
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    isTabletScreen,
    registerMenu,
    currentLocation,
    isSticky,
    isCheckoutPage,
    isSmallMobileScreen,
    isMobileScreen,
}: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    // Hide main navigation
    useEffect(() => {
        setTimeout(() => hideAll());
    }, [isSticky, currentLocation]);

    const afterHeader = React.createRef<HTMLSpanElement>();

    // XNG-Changes: structure
    return (
        <>
            {/* XNG-Changes: Adding logo  */}
            <StyledWrapper {...styles.websiteName}>
                <Zone contentId={id} zoneName="Logo" />
            </StyledWrapper>
            <Page as="header" isHeader={true} {...styles.page} id="header-page">
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    <StyledWrapper {...logoLinksSearchStyles.container} {...{ isTabletScreen, isCheckoutPage }}>
                        {isTabletScreen && !isMobileScreen && !isCheckoutPage && (
                            <Zone contentId={id} zoneName="MainNavigation" normalWidth />
                        )}
                        {isMobileScreen ? (
                            <StyledWrapper {...logoLinksSearchStyles.mobile?.container}>
                                <StyledWrapper {...logoLinksSearchStyles.mobile?.logoMenu}>
                                    <Zone contentId={id} zoneName="Logo" />
                                </StyledWrapper>
                                {isCheckoutPage ? (
                                    <CheckoutHeaderItem />
                                ) : (
                                    <StyledWrapper {...logoLinksSearchStyles.mobile?.actionsWrapper}>
                                        <Zone contentId={id} zoneName="Actions" />
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        ) : (
                            <>
                                <StyledWrapper {...logoLinksSearchStyles.logoWrapper}>
                                    <Zone contentId={id} zoneName="Logo" />
                                </StyledWrapper>
                                {isCheckoutPage ? (
                                    <CheckoutHeaderItem />
                                ) : (
                                    <StyledWrapper {...logoLinksSearchStyles.actionsContainer}>
                                        <StyledWrapper {...logoLinksSearchStyles.searchWrapper}>
                                            <Zone contentId={id} zoneName="Search" />
                                        </StyledWrapper>
                                        <StyledWrapper {...logoLinksSearchStyles.actionsWrapper}>
                                            <Zone contentId={id} zoneName="Actions" />
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                            </>
                        )}
                    </StyledWrapper>
                    {!isCheckoutPage && (
                        <StyledWrapper {...styles.container}>
                            {isMobileScreen ? (
                                <StyledWrapper>
                                    <Zone contentId={id} zoneName="MainNavigation" normalWidth />
                                    <Zone contentId={id} zoneName="Search" />
                                </StyledWrapper>
                            ) : (
                                !isTabletScreen && (
                                    <StyledWrapper className="desktop-view">
                                        <Zone contentId={id} zoneName="MainNavigation" />
                                        <XngZone
                                            hint="Zone For Header Link List"
                                            contentId={id}
                                            zoneName="ExtraLinks"
                                        />
                                    </StyledWrapper>
                                )
                            )}
                        </StyledWrapper>
                    )}
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <XngUtilities />
                <XngLoadingOverlay />
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
            <Transition
                mountOnEnter
                unmountOnExit
                in={registerMenu && registerMenu.length > 0}
                timeout={{ enter: 75, exit: 250 }}
            >
                {state => (
                    <StyledOverlay
                        className={registerMenu && isTabletScreen ? "header-overlay" : ""}
                        transitionState={state}
                    />
                )}
            </Transition>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;

const StyledOverlay = styled.div<{ transitionState: TransitionStatus } & ThemeProps<BaseTheme>>`
    position: fixed;
    right: 0;
    bottom: 0;
    top: 0;
    left: 0;
    background-color: ${color("common.ui8", 0.66)};
    z-index: ${({ theme }: any) => {
        return theme.zIndex.styledOverlay;
    }};
    transition: opacity ease-in-out 250ms;
    ${({ transitionState }) => {
        switch (transitionState) {
            case "entering":
                return "opacity: 0;";
            case "entered":
                return "opacity: 1;";
            case "exiting":
            case "exited":
                return "opacity: 0;";
            default:
                return "";
        }
    }}
`;
